import { Button, Flex, HStack, Image, Spinner, Text, VStack } from "@chakra-ui/react";
import { useAtom } from "jotai";
import { AppStateAtom } from "../../store";
import { useSubscriptionController } from "./useSubscriptionStatusController";
import { useEffect, useState } from "react";
import { PrimaryButton } from "../design_library";
import { NegativeButton } from "../design_library/NegativeButton";
import { checkToken, getStripePortal } from "../../api";
import { track, EVENTS } from "../../utils/analytics";
import { RiExternalLinkLine } from "react-icons/ri";
import React from "react";
import {
  BillingPlan,
  consumePendingSubscriptionPlan,
  getStoredSubscriptionPlan,
  setStoredSubscriptionPlan,
} from "../../utils/subscriptionPlan";

const PLAN_COPY: Record<
  BillingPlan,
  {
    title: string;
    price: string;
    description: string;
    detail: string;
  }
> = {
  monthly: {
    title: "Monthly",
    price: "$59.99/mo",
    description: "Flexible monthly billing with a 45-day free trial.",
    detail: "Billed month-to-month. Cancel anytime.",
  },
  annual: {
    title: "Annual",
    price: "$299.99/yr",
    description: "Best value at $25.00/mo with a 45-day free trial.",
    detail: "Save $419.89 per year compared with monthly billing.",
  },
};

export const SubscriptionStatus = () => {
  const [appState, setAppState] = useAtom(AppStateAtom);
  const controller = useSubscriptionController();
  const [cancelButtonClicked, setCancelButtonClicked] = useState(false);
  const [subscriptionSpinner, setSubscriptionSpinner] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<BillingPlan>(
    () => getStoredSubscriptionPlan() || "monthly"
  );

  useEffect(() => {
    setStoredSubscriptionPlan(selectedPlan);
  }, [selectedPlan]);

  const checkTokenAndRedirect = async () => {
    const token = await checkToken();
    const urlParams = new URLSearchParams(window.location.search);
    const result = urlParams.get("result");
    if (result === "paid" && appState.subscriptionActive !== true) {
      controller.checkSubscription();
      track(EVENTS.SUBSCRIPTION.SUCCESS);
    } else if (result === "cancelled") {
      setAppState((state) => ({ ...state, subscriptionActive: false }));
      track(EVENTS.SUBSCRIPTION.CANCELLED);

      setCancelButtonClicked(true);
      setCheckingSubscription(false);
    } else if (!token.user.subscriptionStatus) {
      const pendingPlan = consumePendingSubscriptionPlan();
      if (pendingPlan) {
        setSelectedPlan(pendingPlan);
        setCheckingSubscription(false);
        controller.subscribe(pendingPlan);
        return;
      }

      setAppState((state) => ({ ...state, subscriptionActive: false }));
      setCheckingSubscription(false);
    } else if (token.user.subscriptionStatus === true) {
      setAppState((state) => ({ ...state, subscriptionActive: true }));
      setCheckingSubscription(false);
    } else {
      setCheckingSubscription(false);
    }
  };

  useEffect(() => {
    checkTokenAndRedirect();
  }, [appState.subscriptionActive]);

  const startSubscription = () => {
    controller.subscribe(selectedPlan);
  };

  const redirect = async () => {
    setSubscriptionSpinner(true);
    try {
      const result = await getStripePortal();
      if (result.success) {
        track(EVENTS.SUBSCRIPTION.REQUEST_PORTAL);
        window.location.href = result.url;
      }
    } catch (error) {
      console.error("Error redirecting to Stripe portal", error);
    } finally {
      setSubscriptionSpinner(false);
    }
  };

  const renderSubscriptionPrompt = () => (
    <VStack
      width={"100vw"}
      height={"100vh"}
      pos={"fixed"}
      top="0"
      left="0"
      backgroundColor={"var(--bg)"}
      opacity={1}
      zIndex={8888}
      display={"flex"}
      justifyContent={"center"}
      alignItems={"center"}
      px="20px"
    >
      <Image
        height={"35"}
        mb="20px"
        src="https://cdn.wealthbee.io/main/logos/wealthbee-logo-with-text.svg"
        zIndex={99999}
      />

      <VStack
        spacing={4}
        width="100%"
        maxW="560px"
        bg="var(--bg)"
        borderRadius="xl"
        p={6}
        border="1px solid var(--border)"
        boxShadow="lg"
        align="stretch"
      >
        <Text fontSize="2xl" fontWeight="semibold" textAlign="center">
          Choose your plan
        </Text>
        <Text fontSize="sm" color="gray.500" textAlign="center">
          Start with a 45-day free trial, then continue on the plan that fits
          you best.
        </Text>

        {(["monthly", "annual"] as BillingPlan[]).map((plan) => {
          const isSelected = selectedPlan === plan;
          return (
            <Button
              key={plan}
              variant="outline"
              justifyContent="flex-start"
              alignItems="stretch"
              height="auto"
              whiteSpace="normal"
              textAlign="left"
              p={4}
              borderWidth="1px"
              borderColor={isSelected ? "var(--accent)" : "var(--border)"}
              bg={isSelected ? "rgba(255, 179, 64, 0.08)" : "transparent"}
              _hover={{
                borderColor: "var(--accent)",
                bg: "rgba(255, 179, 64, 0.08)",
              }}
              onClick={() => setSelectedPlan(plan)}
            >
              <VStack align="start" spacing={1} width="100%">
                <HStack width="100%" justifyContent="space-between">
                  <Text fontWeight="semibold">{PLAN_COPY[plan].title}</Text>
                  <Text fontWeight="bold">{PLAN_COPY[plan].price}</Text>
                </HStack>
                <Text fontSize="sm">{PLAN_COPY[plan].description}</Text>
                <Text fontSize="xs" color="gray.500">
                  {PLAN_COPY[plan].detail}
                </Text>
              </VStack>
            </Button>
          );
        })}

        <PrimaryButton
          px="100px"
          isLoading={controller.loading}
          py="20px"
          onClick={startSubscription}
        >
          Start 45 day free trial
        </PrimaryButton>

        {cancelButtonClicked && (
          <NegativeButton
            bg="transparent"
            size="xs"
            mt="20px"
            onClick={() => {
              if (window.confirm("Are you sure you want to log out?")) {
                window.localStorage.removeItem("token");
                window.location.href = "../app/login";
              }
            }}
          >
            Log out
          </NegativeButton>
        )}
      </VStack>
    </VStack>
  );

  if (cancelButtonClicked) {
    return renderSubscriptionPrompt();
  } else if (checkingSubscription) {
    return (
      <>
        <VStack
          width={"100vw"}
          height={"100vh"}
          pos={"fixed"}
          top="0"
          left="0"
          backgroundColor={"var(--bg)"}
          opacity={1}
          zIndex={8888}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Image
            height={"35"}
            mb="20px"
            src="https://cdn.wealthbee.io/main/logos/wealthbee-logo-with-text.svg"
            zIndex={99999}
          />
          <Spinner size="xl" />
        </VStack>
      </>
    );
  } else if (appState.subscriptionActive !== true) {
    return renderSubscriptionPrompt();
  } else {
    return (
      <Flex direction={"column"} width="100%">
        <HStack
          justifyContent={"space-between"}
          bg="var(--bg)"
          borderRadius="md"
          p={4}
        >
          <Flex direction={"column"}>
            <Text fontWeight={"semibold"}>Manage Your Subscription</Text>
            <Text fontSize={"sm"} color="gray.500">
              Upgrade, change billing details, or manage your account
            </Text>
          </Flex>
          <Button
            rightIcon={<RiExternalLinkLine />}
            bg="var(--accent)"
            color="white"
            shadow="md"
            _hover={{ bg: "var(--accent)", opacity: 0.9 }}
            onClick={redirect}
            isLoading={subscriptionSpinner}
          >
            Billing Portal
          </Button>
        </HStack>
      </Flex>
    );
  }
};
